// src/components/lesson/MiniCheck.tsx
//
// A single SAT-style question rendered between the explanation and
// worked examples. Tests whether the concept just landed. Pure
// client-side check — the answer + explanation are in the page payload
// (lessons are public, no integrity issue).
//
// MCQ: pick a letter, click Check, see green/red feedback + explanation.
// SPR: type a numeric answer, click Check, compare to canonical answer.

'use client'

import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import remarkMath from 'remark-math'
import rehypeKatex from 'rehype-katex'
import { CheckCircle2, XCircle, HelpCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { compareSprAnswer } from '@/lib/test-engine/spr-answer'
import { sprEntryProblem } from '@/lib/sat/answer-validation'
import SprEntryError from '@/components/sat/SprEntryError'
import { normalizeMathMarkdown } from '@/lib/render/normalize-math-markdown'

type ChoiceItem = string | { label: string; text: string }

export type MiniCheckQuestion = {
  id: string
  stem: string
  choices: unknown // raw JSON from DB
  answer: string
  explanation: string | null
  itemType: string | null
}

export default function MiniCheck({
  prompt,
  question,
}: {
  prompt?: string
  question: MiniCheckQuestion
}) {
  const [selected, setSelected] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)
  const [sprAnswer, setSprAnswer] = useState('')
  // Why Check did nothing: a grid-in entry the grader can't read ("abc",
  // "1/0"). The same rule (sprEntryProblem) every runner applies — it is an
  // input error, never a wrong answer.
  const [entryError, setEntryError] = useState<string | null>(null)

  const choices: ChoiceItem[] = Array.isArray(question.choices)
    ? (question.choices as ChoiceItem[])
    : []
  const isSpr = question.itemType === 'SPR' || (choices.length === 0 && !!question.answer)

  function getChoiceText(c: ChoiceItem): string {
    if (typeof c === 'string') return c
    return c.text ?? ''
  }
  function getChoiceLabel(c: ChoiceItem, i: number): string {
    if (typeof c === 'string') return String.fromCharCode(65 + i)
    return c.label ?? String.fromCharCode(65 + i)
  }

  let isCorrect: boolean | null = null
  if (submitted) {
    if (isSpr) {
      // SPR: compare normalized strings + accept fraction equivalents
      isCorrect = compareSprAnswer(sprAnswer.trim(), question.answer)
    } else {
      isCorrect = selected === question.answer
    }
  }

  function reset() {
    setSelected(null)
    setSubmitted(false)
    setSprAnswer('')
    setEntryError(null)
  }

  function check() {
    if (isSpr) {
      const problem = sprEntryProblem(sprAnswer)
      if (problem) {
        setEntryError(problem)
        return
      }
    }
    setEntryError(null)
    setSubmitted(true)
  }

  return (
    <section className="my-6 rounded-xl border border-brand/30 bg-brand/10 dark:bg-brand/15 p-5">
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-brand-ink">
        <HelpCircle className="h-4 w-4" />
        Quick check
      </div>
      {prompt && <p className="mb-3 text-sm text-brand-ink/90 italic">{prompt}</p>}

      {/* Stem */}
      <div className="prose dark:prose-invert max-w-none text-foreground prose-strong:text-brand-ink dark:prose-strong:text-brand-ink prose-code:rounded prose-code:bg-slate-100 dark:prose-code:bg-card prose-code:px-1 prose-code:py-0.5 prose-code:text-brand-ink dark:prose-code:text-brand-ink prose-code:before:content-none prose-code:after:content-none">
        <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
          {normalizeMathMarkdown(question.stem)}
        </ReactMarkdown>
      </div>

      {/* Input */}
      <div className="mt-3">
        {isSpr ? (
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <label htmlFor={`spr-${question.id}`} className="text-sm text-muted-foreground">
                Your answer:
              </label>
              <input
                id={`spr-${question.id}`}
                type="text"
                inputMode="text"
                value={sprAnswer}
                onChange={(e) => {
                  setSprAnswer(e.target.value)
                  setEntryError(null)
                }}
                disabled={submitted}
                aria-invalid={entryError ? true : undefined}
                aria-describedby={
                  entryError ? `spr-error-${question.id}` : `spr-hint-${question.id}`
                }
                className="w-32 rounded-md border border-border bg-card px-3 py-1.5 text-sm text-foreground disabled:opacity-70"
                placeholder="e.g. 5 or 3/4"
              />
            </div>
            <SprEntryError id={`spr-error-${question.id}`} message={entryError} className="mt-1" />
            {/* #48: state the accepted numeric formats for screen readers + sighted users. */}
            <p id={`spr-hint-${question.id}`} className="mt-1 text-xs text-muted-foreground">
              Enter a whole number, fraction (e.g. 3/4), or decimal (e.g. .75).
            </p>
          </div>
        ) : (
          <div className="space-y-1.5">
            {choices.map((c, i) => {
              const label = getChoiceLabel(c, i)
              const text = getChoiceText(c)
              const isSelected = selected === label
              const isCorrectChoice = label === question.answer

              let cls =
                'flex items-start gap-3 rounded-md border px-3 py-2 text-sm transition-colors cursor-pointer '
              if (submitted) {
                if (isCorrectChoice) {
                  cls +=
                    'border-emerald-500/50 bg-emerald-500/10 text-emerald-900 dark:text-emerald-100'
                } else if (isSelected && !isCorrectChoice) {
                  cls += 'border-red-500/50 bg-red-500/10 text-red-900 dark:text-red-100'
                } else {
                  cls += 'border-border bg-card/40 text-muted-foreground'
                }
              } else if (isSelected) {
                cls += 'border-brand/60 bg-brand/10 text-brand-ink'
              } else {
                cls += 'border-border bg-card/40 text-foreground hover:border-border'
              }

              return (
                <label key={label} className={cls}>
                  <input
                    type="radio"
                    name={`mc-${question.id}`}
                    value={label}
                    checked={isSelected}
                    onChange={() => !submitted && setSelected(label)}
                    disabled={submitted}
                    className="mt-1"
                  />
                  <span className="font-mono text-xs font-bold text-muted-foreground">
                    {label}.
                  </span>
                  <span className="flex-1 prose dark:prose-invert prose-sm max-w-none">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm, remarkMath]}
                      rehypePlugins={[rehypeKatex]}
                    >
                      {text}
                    </ReactMarkdown>
                  </span>
                </label>
              )
            })}
          </div>
        )}
      </div>

      {/* Submit / Reset */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        {!submitted ? (
          <Button
            onClick={check}
            disabled={isSpr ? sprAnswer.trim() === '' : selected === null}
            className="bg-brand text-on-brand hover:bg-brand-hover"
          >
            Check answer
          </Button>
        ) : (
          <Button onClick={reset} variant="outline">
            Try again
          </Button>
        )}
      </div>

      {/* Feedback */}
      {submitted && (
        <div
          className={
            'mt-4 rounded-md border p-3 ' +
            (isCorrect
              ? 'border-emerald-500/40 bg-emerald-500/10'
              : 'border-red-500/40 bg-red-500/10')
          }
        >
          <div className="flex items-center gap-2 text-sm font-semibold">
            {isCorrect ? (
              <>
                <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                <span className="text-emerald-800 dark:text-emerald-200">Correct!</span>
              </>
            ) : (
              <>
                <XCircle className="h-4 w-4 text-red-400" />
                <span className="text-red-800 dark:text-red-200">
                  Not quite — the answer is <span className="font-mono">{question.answer}</span>.
                </span>
              </>
            )}
          </div>
          {question.explanation && (
            <div className="mt-2 prose dark:prose-invert prose-sm max-w-none text-foreground">
              <ReactMarkdown remarkPlugins={[remarkGfm, remarkMath]} rehypePlugins={[rehypeKatex]}>
                {question.explanation}
              </ReactMarkdown>
            </div>
          )}
        </div>
      )}
    </section>
  )
}
